import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Header } from "@/components/site/Header";
import { WhatsAppButton } from "@/components/ui/whatsapp-button";
import { ScrollToTop } from "@/components/ScrollToTop";

// Lazy load all pages for better performance
const Index = lazy(() => import("./pages/Index"));
const AboutData = lazy(() => import("./pages/AboutData"));
const AboutSmart = lazy(() => import("./pages/AboutSmart"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const MultimodalAI = lazy(() => import("./pages/MultimodalAI"));
const ComputerVision = lazy(() => import("./pages/ComputerVision"));
const NaturalLanguageProcessing = lazy(() => import("./pages/NaturalLanguageProcessing"));
const AudioAI = lazy(() => import("./pages/AudioAI"));
const PredictiveAnalytics = lazy(() => import("./pages/PredictiveAnalytics"));
const MLOps = lazy(() => import("./pages/MLOps"));
const ResponsibleAI = lazy(() => import("./pages/ResponsibleAI"));
const ErpSystems = lazy(() => import("./pages/ErpSystems"));
const IMS = lazy(() => import("./pages/IMS"));
const EMS = lazy(() => import("./pages/EMS"));
const WebMobileApps = lazy(() => import("./pages/WebMobileApps"));
const ChatbotsAssistants = lazy(() => import("./pages/ChatbotsAssistants"));
const DocumentAutomation = lazy(() => import("./pages/DocumentAutomation"));
const SearchRetrieval = lazy(() => import("./pages/SearchRetrieval"));
const DigitalTwins = lazy(() => import("./pages/DigitalTwins"));
const DataLabeling = lazy(() => import("./pages/DataLabeling"));
const PipelinesDashboards = lazy(() => import("./pages/PipelinesDashboards"));
const CloudEdgeAI = lazy(() => import("./pages/CloudEdgeAI"));
const DatasetDevelopment = lazy(() => import("./pages/DatasetDevelopment"));
const EnterpriseAIConsulting = lazy(() => import("./pages/EnterpriseAIConsulting"));
const Government = lazy(() => import("./pages/Government"));
const Healthcare = lazy(() => import("./pages/Healthcare"));
const Finance = lazy(() => import("./pages/Finance"));
const SmartCities = lazy(() => import("./pages/SmartCities"));
const Tourism = lazy(() => import("./pages/Tourism"));
const Education = lazy(() => import("./pages/Education"));
const Retail = lazy(() => import("./pages/Retail"));
const Security = lazy(() => import("./pages/Security"));
const PrivateSector = lazy(() => import("./pages/PrivateSector"));
const Hotels = lazy(() => import("./pages/Hotels"));
const AboutAI = lazy(() => import("./pages/AboutAI"));
const AIConsulting = lazy(() => import("./pages/AIConsulting"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const Blog = lazy(() => import("./pages/Blog"));
const Careers = lazy(() => import("./pages/Careers"));
const DataDeletion = lazy(() => import("./pages/DataDeletion"));
const Privacy = lazy(() => import("./pages/Privacy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const TQAYFooter = lazy(() => import("@/components/TQAYFooter"));

const queryClient = new QueryClient();

// Loading fallback component - Instant, minimal placeholder
const PageLoader = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50" />
);

const AppLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="min-h-screen bg-gradient-background">
      <Header />
      <main className="m-0 p-0 pt-24">{children}</main>
      <Suspense fallback={<div className="h-64" />}>
        <TQAYFooter />
      </Suspense>
      <WhatsAppButton phoneNumber="+97466655096" />
    </div>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <AppLayout>
          <Suspense fallback={<PageLoader />}>
            <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/about" element={<About />} />
            <Route path="/ai-consulting" element={<AIConsulting />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about-ai" element={<AboutAI />} />
            <Route path="/multimodal-ai" element={<MultimodalAI />} />
            <Route path="/computer-vision" element={<ComputerVision />} />
            <Route path="/natural-language-processing" element={<NaturalLanguageProcessing />} />
            <Route path="/audio-ai" element={<AudioAI />} />
            <Route path="/predictive-analytics" element={<PredictiveAnalytics />} />
            <Route path="/mlops" element={<MLOps />} />
            <Route path="/responsible-ai" element={<ResponsibleAI />} />
            <Route path="/erp-systems" element={<ErpSystems />} />
            <Route path="/ims" element={<IMS />} />
            <Route path="/ems" element={<EMS />} />
            <Route path="/web-mobile-apps" element={<WebMobileApps />} />
            <Route path="/chatbots-assistants" element={<ChatbotsAssistants />} />
            <Route path="/document-automation" element={<DocumentAutomation />} />
            <Route path="/search-retrieval" element={<SearchRetrieval />} />
            <Route path="/digital-twins" element={<DigitalTwins />} />
            <Route path="/data-labeling" element={<DataLabeling />} />
            <Route path="/pipelines-dashboards" element={<PipelinesDashboards />} />
            <Route path="/cloud-edge-ai" element={<CloudEdgeAI />} />
            <Route path="/dataset-development" element={<DatasetDevelopment />} />
            <Route path="/enterprise-ai-consulting" element={<EnterpriseAIConsulting />} />
            <Route path="/government" element={<Government />} />
            <Route path="/healthcare" element={<Healthcare />} />
            <Route path="/finance" element={<Finance />} />
            <Route path="/smart-cities" element={<SmartCities />} />
            <Route path="/tourism" element={<Tourism />} />
            <Route path="/education" element={<Education />} />
            <Route path="/retail" element={<Retail />} />
            <Route path="/security" element={<Security />} />
            <Route path="/private-sector" element={<PrivateSector />} />
            <Route path="/hotels" element={<Hotels />} />
            <Route path="/about-data" element={<AboutData />} />
            <Route path="/about-smart" element={<AboutSmart />} />
            <Route path="/case-studies" element={<CaseStudies />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/careers" element={<Careers />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </AppLayout>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
